import LaunchIcon from "@mui/icons-material/Launch";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { runShiftBidAward } from "../../api/bidAward";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import {
  clearAwardData,
  processBidAwardPayload,
  processBidIAOAwardPayload,
  unAssignBidGroup,
  unAssignBidLines,
} from "../../util/bid/bidAward";
import { exportNoBidIAOToCsv, generateNoBidIAOExportFilename } from "../../util/bid/bidAwardExport";
import {
  getEmployeeFtCount,
  getEmployeePtCount,
  getS3ObjectName,
  isAwardedBid,
  longPollingBidFetch,
} from "../../util/bid/bidCommon";
import { BID_AWARDED_FILTER_OPTIONS, BID_AWARD_TYPE, BID_STATUS } from "../../util/bid/bidConstants";
import { buildBidS3Data } from "../../util/bid/bidEventBuilder";
import {
  getAwardTabBidLinesColumns,
  getAwardTabNoBidEmpColumns,
  getBlockLinesCount,
  getEmployeeErrorsCount,
  hasUnawardedEmployees,
  getUnAssignedBlockLinesCount,
} from "../../util/bid/shiftBidUtils";
import SuccessDialog from "../fundamentals/SuccessDialog";

import { getPresignedUrl, uploadFileToS3 } from "../../api/system.js";
import { getEligibleEmployees, getEmployeeUnrankedCount, getIneligibleEmployees } from "../../util/bid/bidGroupUtils";
import { copyJsonObject, sanitizeSpecialChars } from "../../util/commonUtils";
import {
  BID_AWARD_NO_BID_PAGE_TYPE,
  BID_LINES_PAGE_TYPE,
  FULL_TIME,
  NON_CONTRACT,
  PART_TIME,
} from "../../util/constants";
import { format_DOW_ShortMonth_Date_Year } from "../../util/dateTimeFormatter";
import { bidDataErrorCodeToErrorMessageMap } from "../../util/errorMapper";
import { BID_PUBLISH_DIALOG_MESSAGE } from "../../util/messageConstants";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper";
import customLoadingTableOverlay from "../CustomLoadingTableOverlay";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay";
import ErrorMessage from "../ErrorMessage";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import StarsCloseIconButton from "../fundamentals/StarsCloseIconButton";
import AssignIneligibleAtOpenEmployeeDetailPanelContent from "./AssignIneligibleAtOpenEmployeeDetailPanelContent";
import AssignNoBidEmployeeDetailPanelContent from "./AssignNoBidEmployeeDetailPanelContent";
import BidAwardExport from "./BidAwardExport";

const ShiftBidAward = (props) => {
  const userSession = useSelector(selectUserSession);
  const [isAddEmployeeConfirmationChecked, setIsAddEmployeeConfirmationChecked] = useState(false);
  const [isRemoveEmployeeConfirmationChecked, setIsRemoveEmployeeConfirmationChecked] = useState(false);
  const [isEligibilityEmployeeConfirmationChecked, setIsEligibilityEmployeeConfirmationChecked] = useState(false);
  const [isAwardRunning, setIsAwardRunning] = useState(false);
  const [awardErrorMessage, setAwardErrorMessage] = useState("");
  const [isIAOAwardRunning, setIsIAOAwardRunning] = useState(false);
  const [IAOAwardErrorMessage, setIAOAwardErrorMessage] = useState("");
  const [selectedAwardFilterRadioButton, setSelectedAwardFilterRadioButton] = useState(BID_AWARDED_FILTER_OPTIONS.ALL);
  const [selectedAwardFilterCheckBoxList, setSelectedAwardFilterCheckBoxList] = useState(Object.values(BID_AWARD_TYPE));
  const [selectedFTPTFilterCheckBoxList, setSelectedFTPTFilterCheckBoxList] = useState([FULL_TIME, PART_TIME]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [filteredBidLines, setFilteredBidLines] = useState([]);
  const [isEnlargeBidLineDialogOpen, setIsEnlargeBidLineDialogOpen] = useState(false);
  const [noBidListDetailPanelExpandedRowIds, setNoBidListDetailPanelExpandedRowIds] = useState([]);
  const [ineligibleAtOpenListDetailPanelExpandedRowIds, setIneligibleAtOpenListDetailPanelExpandedRowIds] = useState(
    []
  );
  const [isReRunAwardWarningDialogOpen, setIsReRunAwardWarningDialogOpen] = useState(false);
  const [isReRunIAOAwardConfirmationDialogOpen, setIsReRunIAOAwardConfirmationDialogOpen] = useState(false);
  const [isIAOOverwriteCheckDialogOpen, setIsIAOOverwriteCheckDialogOpen] = useState(false);
  const [isNoBidListChanged, setIsNoBidListChanged] = useState(false);
  const [isPublishConfirmationDialogOpen, setIsPublishConfirmationDialogOpen] = useState(false);
  const [showPublishLoader, setShowPublishLoader] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [publishErrors, setPublishErrors] = useState("");

  // EXPORT RESULTS
  const [exportResultsMenuAnchor, setExportResultsMenuAnchor] = useState(null);

  // Bid Line Filter useEffect
  useEffect(() => {
    const filterByRadioButtons = (filterValue) => {
      let filteredLines = [];
      switch (filterValue) {
        case BID_AWARDED_FILTER_OPTIONS.ALL:
          filteredLines = props.bidLines;
          break;
        case BID_AWARDED_FILTER_OPTIONS.AWARDED:
          filteredLines = props.bidLines.filter((line) => line.awardedEid);
          break;
        case BID_AWARDED_FILTER_OPTIONS.UNAWARDED:
          filteredLines = props.bidLines.filter((line) => !line.awardedEid);
          break;
        default:
          console.error("Unrecognized filter option");
      }
      setFilteredBidLines(filteredLines);
      return filteredLines;
    };

    const filterByAwardTypeCheckBoxes = (filteredLines, awardTypeList) => {
      const awardTypeFilteredLines = filteredLines.filter((line) =>
        line.awardedEid ? awardTypeList.some((item) => line.awardType?.startsWith(item)) : true
      );
      setFilteredBidLines(awardTypeFilteredLines);
      return awardTypeFilteredLines;
    };

    const filterByFTPTTypeCheckBoxes = (filteredLines, ftptList) => {
      const ftptFilteredLines = filteredLines.filter((line) => ftptList.includes(line.ftpt));
      setFilteredBidLines(ftptFilteredLines);
    };

    let filteredLines = filterByRadioButtons(selectedAwardFilterRadioButton);
    filteredLines = filterByAwardTypeCheckBoxes(filteredLines, selectedAwardFilterCheckBoxList);
    filterByFTPTTypeCheckBoxes(filteredLines, selectedFTPTFilterCheckBoxList);
  }, [props.bidLines, selectedAwardFilterRadioButton, selectedAwardFilterCheckBoxList, selectedFTPTFilterCheckBoxList]);

  useEffect(() => {
    const clickedEmployeeIds = props.noBidList.filter((bidItem) => bidItem.isExpanded).map((bidItem) => bidItem.id);
    setNoBidListDetailPanelExpandedRowIds(clickedEmployeeIds);
  }, [props.noBidList]);

  useEffect(() => {
    if (isNoBidListChanged) {
      clearIAOAward();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNoBidListChanged]);

  useEffect(() => {
    const clickedEmployeeIds = props.ineligibleAtOpenList
      .filter((bidItem) => bidItem.isExpanded)
      .map((bidItem) => bidItem.id);
    setIneligibleAtOpenListDetailPanelExpandedRowIds(clickedEmployeeIds);
  }, [props.ineligibleAtOpenList]);

  const isIAOAwardProcessed = () => {
    for (const emp of props.ineligibleAtOpenList) {
      if (emp.awardedLine || emp.isManualIAOEntryRequired) {
        return true;
      }
    }
    return false;
  };

  const getNoBidDetailPanelContent = React.useCallback(
    ({ row }) =>
      row.isExpanded &&
      !isAwardedBid(props.bid.status) && (
        <AssignNoBidEmployeeDetailPanelContent
          employee={row}
          bidLines={props.bidLines}
          setBidLines={props.setBidLines}
          bidGroup={props.bidGroup}
          setBidGroup={props.setBidGroup}
          noBidList={props.noBidList}
          setIsNoBidListChanged={setIsNoBidListChanged}
          isIAOOverwriteCheckRequired={isIAOOverwriteCheckRequired}
        />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.noBidList, props.ineligibleAtOpenList]
  );

  const getIneligibleAtOpenDetailPanelContent = React.useCallback(
    ({ row }) =>
      row.isExpanded &&
      !isAwardedBid(props.bid.status) && (
        <AssignIneligibleAtOpenEmployeeDetailPanelContent
          employee={row}
          bidLines={props.bidLines}
          setBidLines={props.setBidLines}
          bidGroup={props.bidGroup}
          setBidGroup={props.setBidGroup}
          ineligibleAtOpenList={props.ineligibleAtOpenList}
        />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.ineligibleAtOpenList]
  );

  const getDetailPanelHeight = React.useCallback(({ row }) => (row.isExpanded ? "auto" : 0), []);

  const getUnassignedNoBidCount = () => {
    const unassignedNoBid = props.noBidList.filter((emp) => !emp.awardedLine);
    return unassignedNoBid.length;
  };

  const handleNoBidListDetailPanelExpandedRowIdsChange = React.useCallback((newIds) => {
    setNoBidListDetailPanelExpandedRowIds(newIds);
  }, []);

  const handleIneligibleAtOpenListDetailPanelExpandedRowIdsChange = React.useCallback((newIds) => {
    setIneligibleAtOpenListDetailPanelExpandedRowIds(newIds);
  }, []);

  const handleNoBidListChevronClicked = (params) => {
    let newBidGroup = copyJsonObject(props.bidGroup);
    const bidGroupIdx = props.bidGroup.findIndex((emp) => emp.id === params.id);
    newBidGroup[bidGroupIdx].isExpanded = !newBidGroup[bidGroupIdx].isExpanded;
    props.setBidGroup(newBidGroup);
  };

  const isIAOOverwriteCheckRequired = () => {
    if (isIAOAwardProcessed()) {
      setIsIAOOverwriteCheckDialogOpen(true);
      return true;
    }
    return false;
  };

  const handleIneligibleAtOpenListChevronClicked = (params) => {
    let newBidGroup = copyJsonObject(props.bidGroup);
    const bidGroupIdx = props.bidGroup.findIndex((emp) => emp.id === params.id);
    newBidGroup[bidGroupIdx].isExpanded = !newBidGroup[bidGroupIdx].isExpanded;
    props.setBidGroup(newBidGroup);
  };

  const handleRunAwardClicked = (bidLines, bidGroup) => {
    setIsAwardRunning(true);
    runShiftBidAward(props.bid.referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        const awardResult = processBidAwardPayload(response, bidLines, bidGroup);
        props.setBid({ ...props.bid, awardLog: awardResult.awardLog });
        props.setBidLines(awardResult.bidLines);
        props.setBidGroup(awardResult.bidGroup);
        setIsAwardRunning(false);
      })
      .catch((error) => {
        console.log("Error: ", error);
        setIsAwardRunning(false);
        setAwardErrorMessage("Unexpected Error");
      });
  };

  const handleReRunAwardClicked = () => {
    setIsReRunAwardWarningDialogOpen(true);
  };

  const handleReRunAwardConfirm = () => {
    setIsReRunAwardWarningDialogOpen(false);
    setSelectedTab(0);
    const clearedDataResult = clearAwardData(props.bidLines, props.bidGroup);
    props.setBidLines(clearedDataResult.bidLines);
    props.setBidGroup(clearedDataResult.bidGroup);
    handleRunAwardClicked(clearedDataResult.bidLines, clearedDataResult.bidGroup);
  };

  const handleRunIAOAwardClicked = (bidLines, bidGroup) => {
    setIsIAOAwardRunning(true);
    runShiftBidAward(props.bid.referenceNumber, props.ineligibleAtOpenList.map((emp) => emp.eid).join(","))
      .then((res) => res.json())
      .then((response) => {
        const awardResult = processBidIAOAwardPayload(response, bidLines, bidGroup, props.ineligibleAtOpenList);
        props.setBid({ ...props.bid, awardLog: [props.bid.awardLog[0], ...awardResult.awardLog] });
        props.setBidLines(awardResult.bidLines);
        props.setBidGroup(awardResult.bidGroup);
        setIsIAOAwardRunning(false);
      })
      .catch((error) => {
        console.log("Error: ", error);
        setIsIAOAwardRunning(false);
        setIAOAwardErrorMessage("Unexpected Error");
      });
  };

  const handleReRunIAOAwardClicked = () => {
    setIsReRunIAOAwardConfirmationDialogOpen(true);
  };

  const clearIAOAward = () => {
    let currentBidLines = copyJsonObject(props.bidLines);
    let currentBidGroup = copyJsonObject(props.bidGroup);
    for (const emp of props.ineligibleAtOpenList) {
      //Clear from bidLines
      const bidLineIdx = currentBidLines.findIndex((line) => line?.awardedEid === emp.eid);
      if (bidLineIdx !== -1) {
        if (currentBidLines[bidLineIdx]?.isDupedLine) {
          currentBidLines.splice(bidLineIdx, 1);
        } else {
          currentBidLines = unAssignBidLines(currentBidLines, bidLineIdx);
        }
      }

      //Clear from bidGroup
      const bidGroupIdx = props.bidGroup.findIndex((employee) => employee?.eid === emp.eid);
      currentBidGroup = unAssignBidGroup(currentBidGroup, bidGroupIdx);
      currentBidGroup[bidGroupIdx].isExpanded = false;
      currentBidGroup[bidGroupIdx].isManualIAOEntryRequired = false;
      // revert IAO-AA/PB/NB/NEC back to IAO when clearing the IAO result.
      currentBidGroup[bidGroupIdx].awardType = BID_AWARD_TYPE.INELIGIBLE_AT_OPEN;
    }
    props.setBidLines(currentBidLines);
    props.setBidGroup(currentBidGroup);
    return { bidLines: currentBidLines, bidGroup: currentBidGroup };
  };

  const handleReRunIAOAwardConfirm = () => {
    const result = clearIAOAward();
    setIsReRunIAOAwardConfirmationDialogOpen(false);
    handleRunIAOAwardClicked(result.bidLines, result.bidGroup);
  };

  const handleIAOOverwriteConfirm = () => {
    clearIAOAward();
    setIsIAOOverwriteCheckDialogOpen(false);
  };

  const handleAwardFilterRadioButtonClicked = (event) => {
    setSelectedAwardFilterRadioButton(event.target.value);
  };

  const handleAwardFilterCheckBoxClicked = (event) => {
    let newFilterCheckBoxList = copyJsonObject(selectedAwardFilterCheckBoxList);
    if (!event.target.checked) {
      newFilterCheckBoxList = newFilterCheckBoxList.filter((filter) => !filter.startsWith(event.target.value));
    } else {
      newFilterCheckBoxList.push(event.target.value);
    }
    setSelectedAwardFilterCheckBoxList(newFilterCheckBoxList);
  };

  const handleFTPTFilterCheckBoxClicked = (event) => {
    let newFilterCheckBoxList = copyJsonObject(selectedFTPTFilterCheckBoxList);
    if (!event.target.checked) {
      newFilterCheckBoxList = newFilterCheckBoxList.filter((filter) => filter !== event.target.value);
    } else {
      newFilterCheckBoxList.push(event.target.value);
    }
    setSelectedFTPTFilterCheckBoxList(newFilterCheckBoxList);
  };

  const handleEnlargeBidLinesButtonClicked = (event) => {
    setIsEnlargeBidLineDialogOpen(true);
  };

  const handleEnlargeBidLinesDialogClosed = (event) => {
    setIsEnlargeBidLineDialogOpen(false);
  };

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const shouldDisablePublishButton = () => {
    return (
      props.isFormChanged ||
      props.isBidGroupTabChanged ||
      hasUnawardedEmployees(props.bidGroup) ||
      isAwardedBid(props.bid.status) ||
      getUnAssignedBlockLinesCount(props.bidLines) > 0
    );
  };

  const getPublishButtonToolTipText = () => {
    const employeesWithNoBidLines = hasUnawardedEmployees(props.bidGroup);

    if (employeesWithNoBidLines && props.isFormChanged) {
      return "Unable to publish. There are unsaved changes and unawarded Employees.";
    }

    if (employeesWithNoBidLines) {
      return "Unable to publish. There are unawarded Employees.";
    }

    if (props.isFormChanged) {
      return "Unable to publish. There are unsaved changes.";
    }

    return "";
  };

  const onPublishSuccess = () => {
    setIsPublishConfirmationDialogOpen(false);
    setShowPublishLoader(false);
    setIsSuccessDialogOpen(true);
  };

  const onPublishError = () => {
    setPublishErrors("Unexpected Error");
    setIsPublishConfirmationDialogOpen(false);
    setShowPublishLoader(false);
    setIsSuccessDialogOpen(true);
  };

  const onPublishConfirm = async () => {
    setShowPublishLoader(true);
    const bidData = { ...props.bid };
    bidData.status = BID_STATUS.AWARDED;
    bidData.scheduledStatus = "";

    // Replace spl. chars with underscore (temp fix for existing bids with spl. chars in the bid name, we can revert it once these bids get awarded)
    bidData.bidName = sanitizeSpecialChars(bidData.bidName);

    try {
      const s3ObjectName = await getS3ObjectName(bidData.pk, bidData.station);
      const res = await getPresignedUrl("put_object", "bid-data", s3ObjectName);
      const result = await res.json();
      if (result.presignedUrl) {
        const bidS3Data = buildBidS3Data(userSession, bidData, props.bidLines, props.bidGroup, s3ObjectName);
        const response = await uploadFileToS3(result.presignedUrl, bidS3Data);

        if (response.status === 200) {
          const requestId = response.headers.get("x-amz-request-id");
          props.currentRequestId.current = requestId;
          await longPollingBidFetch(bidData.referenceNumber, userSession, requestId);
          onPublishSuccess();
        } else {
          const errorMessage = "Bad response from uploadFileToS3";
          console.error(errorMessage);
          throw Error(errorMessage);
        }
      } else {
        const errorMessage = "Bad response from getPresignedUrl";
        console.error(errorMessage);
        throw Error(errorMessage);
      }
    } catch (err) {
      console.error("Error in onPublishConfirm: ", err);
      onPublishError();
    }
  };

  const isBidAwardAvailable = () => {
    for (const emp of props.bidGroup) {
      if ("awardType" in emp) {
        return true;
      }
    }
    return false;
  };

  const handleNoBidIAOExportBtnClick = (awardType) => {
    try {
      exportNoBidIAOToCsv({
        filename: generateNoBidIAOExportFilename({
          station: props.bid.station,
          workgroup: props.bid.workgroup,
          bidType: props.bid.bidType,
          effectiveStartDate: props.bid.schedStartDate,
          effectiveEndDate: props.bid.schedEndDate,
          awardType,
        }),
        rows: awardType === "IAO" ? props.ineligibleAtOpenList : props.noBidList,
      });
    } catch (e) {
      console.error(`Error during downloading ${awardType} csv!`, e);
    }
  };

  const renderBidGroupChangesDetectedAlert = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Bid Group changes must be saved before you can run the award. This will delete any existing award results.
        </Alert>
      </Grid>
    );
  };

  const renderBidLineChangesDetectedAlert = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Bid Line changes must be saved before you can run the award. This will delete the existing award results.
        </Alert>
      </Grid>
    );
  };

  const renderUnAssignedBlockLinesAlert = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Unassigned block lines found. Please update the block lines before running the award.
        </Alert>
      </Grid>
    );
  };

  const renderPublishButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        component="label"
        onClick={() => setIsPublishConfirmationDialogOpen(true)}
        disabled={shouldDisablePublishButton()}
        sx={{ margin: "5px" }}
      >
        Publish
      </Button>
    );
  };

  const renderAwardIAOButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        startIcon={isIAOAwardRunning ? null : <ListAltOutlinedIcon />}
        component="label"
        onClick={
          isIAOAwardProcessed()
            ? handleReRunIAOAwardClicked
            : () => handleRunIAOAwardClicked(props.bidLines, props.bidGroup)
        }
        disabled={
          props.ineligibleAtOpenList.length <= 0 ||
          getUnassignedNoBidCount() > 0 ||
          isIAOAwardRunning ||
          isNoBidListChanged ||
          props.isBidGroupTabChanged ||
          props.isBidLineTabChanged ||
          isAwardedBid(props.bid.status) ||
          getUnAssignedBlockLinesCount(props.bidLines) > 0
        }
        sx={{ margin: "5px", width: "200px" }}
      >
        {isIAOAwardRunning ? <CircularProgress size={25} /> : isIAOAwardProcessed() ? "Re-Run Award IAO" : "Award IAO"}
      </Button>
    );
  };

  const renderPreAwardChecks = () => {
    return (
      <>
        {/* Title */}
        <Grid container sx={{ padding: "10px" }}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ textAlign: "left", fontWeight: "bold" }}>
              {props.bid.station} | {props.bid.workgroup}{" "}
              {props.schedDates[0] !== null &&
                props.schedDates[1] !== null &&
                `| ${format_DOW_ShortMonth_Date_Year(props.schedDates[0])} - ${format_DOW_ShortMonth_Date_Year(
                  props.schedDates[1]
                )}`}
              {props.bidName && ` | ${props.bidName}`}
            </Typography>
          </Grid>
        </Grid>

        {/* Bid Line/Bid Group Counts Section */}
        <Grid container sx={{ bgcolor: "secondary.swaLightestBlue", padding: "10px" }} alignItems={"center"}>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "left" }}>
            <Box sx={{ padding: "5px" }}>
              <Grid container>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        Bid Lines
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      Total:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {props.bidLines.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      FT:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getEmployeeFtCount(props.bidLines)}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      PT:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getEmployeePtCount(props.bidLines)}
                      </Typography>
                    </Grid>
                    {getUnionContractGivenWorkgroup(props.bid.workgroup) === NON_CONTRACT && (
                      <Grid item xs={2} sx={{ textAlign: "left" }}>
                        Block Lines:{" "}
                        <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                          {getBlockLinesCount(props.bidLines)}
                        </Typography>
                      </Grid>
                    )}
                    {getEmployeeErrorsCount(props.bidLines) > 0 && (
                      <Grid item xs={2} sx={{ textAlign: "left", color: "primary.swaRed" }}>
                        Errors:{" "}
                        <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                          {getEmployeeErrorsCount(props.bidLines)}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        Eligible
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      Total:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getEligibleEmployees(props.bidGroup).length}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      FT:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getEmployeeFtCount(getEligibleEmployees(props.bidGroup))}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      PT:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getEmployeePtCount(getEligibleEmployees(props.bidGroup))}
                      </Typography>
                    </Grid>
                    <Grid item xs={3} sx={{ textAlign: "left" }}>
                      Unranked:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getEmployeeUnrankedCount(getEligibleEmployees(props.bidGroup))}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        Ineligible
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      Total:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getIneligibleEmployees(props.bidGroup).length}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      FT:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getEmployeeFtCount(getIneligibleEmployees(props.bidGroup))}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ textAlign: "left" }}>
                      PT:{" "}
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        {getEmployeePtCount(getIneligibleEmployees(props.bidGroup))}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}></Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* Checklist */}
        <Grid container sx={{ padding: "1rem", textAlign: "left" }}>
          <Grid item xs={12}>
            <Typography sx={{ color: "primary.swaBlue", fontWeight: "bold" }}>
              Check off each item as complete before running the Award:
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ padding: "10px" }}>
            <FormControl required component="fieldset">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAddEmployeeConfirmationChecked}
                    onChange={(event) => setIsAddEmployeeConfirmationChecked(event.target.checked)}
                    name="addEmployeeCheck"
                    color="primary"
                  />
                }
                label="Add any Employees who entered the Workgroup during the Bid Window"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ padding: "10px" }}>
            <FormControl required component="fieldset">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRemoveEmployeeConfirmationChecked}
                    onChange={(event) => setIsRemoveEmployeeConfirmationChecked(event.target.checked)}
                    name="removeEmployeeCheck"
                    color="primary"
                  />
                }
                label="Remove any Employees who left the Workgroup after the Bid opened"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ padding: "10px" }}>
            <FormControl required component="fieldset">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isEligibilityEmployeeConfirmationChecked}
                    onChange={(event) => setIsEligibilityEmployeeConfirmationChecked(event.target.checked)}
                    name="eligibilityEmployeeCheck"
                    color="primary"
                  />
                }
                label="Check eligibility of all Employees"
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ padding: "10px" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleRunAwardClicked(props.bidLines, props.bidGroup)}
              size="large"
              sx={{ width: "200px" }}
              startIcon={isAwardRunning ? null : <ListAltOutlinedIcon />}
              disabled={
                !isAddEmployeeConfirmationChecked ||
                !isRemoveEmployeeConfirmationChecked ||
                !isEligibilityEmployeeConfirmationChecked ||
                isAwardRunning ||
                props.isBidGroupTabChanged ||
                props.isBidLineTabChanged ||
                getUnAssignedBlockLinesCount(props.bidLines) > 0
              }
            >
              {isAwardRunning ? <CircularProgress size={25} /> : "Run Award"}
            </Button>
          </Grid>
          {awardErrorMessage !== "" && (
            <ErrorMessage
              errorMessage={bidDataErrorCodeToErrorMessageMap.bidAwardError.message}
              errorCode={bidDataErrorCodeToErrorMessageMap.bidAwardError.code}
            />
          )}
        </Grid>
      </>
    );
  };

  const renderPostAwardResults = () => {
    return (
      <Grid container>
        {/* Station | Workgroup & Export/Print buttons */}
        <Grid
          item
          xs={6}
          sx={{
            borderBottom: "1px solid",
            borderColor: "secondary.swaGray2",
            paddingLeft: "1rem",
            paddingTop: "15px",
            paddingBottom: "15px",
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "left", fontWeight: "bold" }}>
            {props.bid.station} | {props.bid.workgroup}{" "}
            {props.schedDates[0] !== null &&
              props.schedDates[1] !== null &&
              `| ${format_DOW_ShortMonth_Date_Year(props.schedDates[0])} - ${format_DOW_ShortMonth_Date_Year(
                props.schedDates[1]
              )}`}
            {props.bidName && ` | ${props.bidName}`}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            paddingRight: "1rem",
            borderBottom: "1px solid",
            color: "secondary.swaGray2",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={isAwardRunning ? null : <ListAltOutlinedIcon />}
            component="label"
            onClick={handleReRunAwardClicked}
            disabled={
              isAwardRunning ||
              props.isBidGroupTabChanged ||
              props.isBidLineTabChanged ||
              isAwardedBid(props.bid.status) ||
              getUnAssignedBlockLinesCount(props.bidLines) > 0
            }
            sx={{ margin: "5px", width: "200px" }}
          >
            {isAwardRunning ? <CircularProgress size={25} /> : "Re-Run Award"}
          </Button>
          <BidAwardExport
            bid={props.bid}
            bidLines={props.bidLines}
            exportResultsMenuAnchor={exportResultsMenuAnchor}
            setExportResultsMenuAnchor={setExportResultsMenuAnchor}
            isFormChanged={props.isFormChanged}
          />
          {props.isFormChanged || hasUnawardedEmployees(props.bidGroup) ? (
            <Tooltip title={getPublishButtonToolTipText()} arrow placement="bottom">
              <span>{renderPublishButton()}</span>
            </Tooltip>
          ) : (
            renderPublishButton()
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            borderBottom: "1px solid",
            borderColor: "secondary.swaGray2",
            paddingLeft: "1rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography variant="title" sx={{ paddingRight: "20px", fontWeight: "bold", color: "primary.swaBlue" }}>
            Key:
          </Typography>
          <Typography variant="title">
            <strong>AA: </strong> Auto-Awarded
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>PB: </strong> Awarded with Permanent Bid
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>NEC: </strong> Not Enough Choices
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>NB: </strong> No Bid
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>IAO: </strong> Ineligible at Open
          </Typography>
          {getUnionContractGivenWorkgroup(props.bid.workgroup) === NON_CONTRACT && (
            <>
              <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
              <Typography variant="title">
                <strong>BL: </strong> Block Line
              </Typography>
            </>
          )}
        </Grid>
        <Grid item xs={7}>
          {renderBidLines(false)}
        </Grid>
        <Grid item xs={5} sx={{ borderLeft: "5px solid", borderColor: "secondary.swaGray2" }}>
          {renderNoBidsSection()}
        </Grid>
      </Grid>
    );
  };

  const renderBidLines = (isDialog) => {
    return (
      <Grid container sx={{ borderRight: "1px solid", borderColor: "secondary.swaGray2" }}>
        <Grid container sx={{ display: "flex", alignItems: "center", padding: "1rem" }}>
          <Grid item xs={2}>
            <Typography variant="h6" color="primary.swaBlue" fontWeight="bold" textAlign="left">
              Bid Lines
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <FormControl>
              <RadioGroup
                value={selectedAwardFilterRadioButton}
                onChange={handleAwardFilterRadioButtonClicked}
                row
                name="row-radio-buttons-group"
                sx={{ alignItems: "center" }}
              >
                <Typography sx={{ marginRight: "10px", fontWeight: "bold", color: "secondary.swaBlack" }}>
                  Show:{" "}
                </Typography>
                <FormControlLabel
                  value={BID_AWARDED_FILTER_OPTIONS.ALL}
                  control={<Radio />}
                  label={`All (${props.bidLines.length})`}
                />
                <FormControlLabel
                  value={BID_AWARDED_FILTER_OPTIONS.AWARDED}
                  control={<Radio />}
                  label={`Awarded (${props.bidLines.filter((line) => !!line.awardedEid).length})`}
                />
                <FormControlLabel
                  value={BID_AWARDED_FILTER_OPTIONS.UNAWARDED}
                  control={<Radio />}
                  label={`Un-awarded (${props.bidLines.filter((line) => !line.awardedEid).length})`}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={1} sx={{ textAlign: "right", paddingRight: "1rem" }}>
            {isDialog ? (
              <StarsCloseIconButton onClick={handleEnlargeBidLinesDialogClosed} />
            ) : (
              <IconButton sx={{ padding: 0 }} onClick={handleEnlargeBidLinesButtonClicked}>
                <LaunchIcon color="primary" />
              </IconButton>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", textAlign: "left" }}>
          <FormControl>
            <FormGroup row sx={{ alignItems: "center", backgroundColor: "secondary.swaLightestBlue" }}>
              <Typography
                sx={{
                  marginRight: "10px",
                  fontWeight: "bold",
                  color: "secondary.swaBlack",
                  fontSize: "14px",
                  paddingLeft: "1rem",
                }}
              >
                Award Filter:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(BID_AWARD_TYPE.AUTO_AWARDED)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={BID_AWARD_TYPE.AUTO_AWARDED}
                  />
                }
                label={BID_AWARD_TYPE.AUTO_AWARDED}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(BID_AWARD_TYPE.PERMANENT_BID)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={BID_AWARD_TYPE.PERMANENT_BID}
                  />
                }
                label={BID_AWARD_TYPE.PERMANENT_BID}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(BID_AWARD_TYPE.NOT_ENOUGH_CHOICES)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={BID_AWARD_TYPE.NOT_ENOUGH_CHOICES}
                  />
                }
                label={BID_AWARD_TYPE.NOT_ENOUGH_CHOICES}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(BID_AWARD_TYPE.NO_BID)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={BID_AWARD_TYPE.NO_BID}
                  />
                }
                label={BID_AWARD_TYPE.NO_BID}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(BID_AWARD_TYPE.INELIGIBLE_AT_OPEN)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={BID_AWARD_TYPE.INELIGIBLE_AT_OPEN}
                  />
                }
                label={BID_AWARD_TYPE.INELIGIBLE_AT_OPEN}
              />
              {getUnionContractGivenWorkgroup(props.bid.workgroup) === NON_CONTRACT && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedAwardFilterCheckBoxList.includes(BID_AWARD_TYPE.BLOCK_LINE)}
                      onChange={handleAwardFilterCheckBoxClicked}
                      value={BID_AWARD_TYPE.BLOCK_LINE}
                    />
                  }
                  label={BID_AWARD_TYPE.BLOCK_LINE}
                />
              )}
              {selectedAwardFilterCheckBoxList.length === 0 && (
                <FormHelperText error>At least one award type must be selected</FormHelperText>
              )}
            </FormGroup>
          </FormControl>
          <FormControl sx={{ borderLeft: "1px solid", borderColor: "secondary.swaLightBlue", flexGrow: 1 }}>
            <FormGroup row sx={{ alignItems: "center", backgroundColor: "secondary.swaLightestBlue" }}>
              <Typography
                sx={{
                  marginRight: "10px",
                  fontWeight: "bold",
                  color: "secondary.swaBlack",
                  fontSize: "14px",
                  paddingLeft: "1rem",
                }}
              >
                FT/PT:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedFTPTFilterCheckBoxList.includes(FULL_TIME)}
                    onChange={handleFTPTFilterCheckBoxClicked}
                    value={FULL_TIME}
                  />
                }
                label={FULL_TIME}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedFTPTFilterCheckBoxList.includes(PART_TIME)}
                    onChange={handleFTPTFilterCheckBoxClicked}
                    value={PART_TIME}
                  />
                }
                label={PART_TIME}
              />
              {selectedFTPTFilterCheckBoxList.length === 0 && (
                <FormHelperText error>At least one FT/PT type must be selected</FormHelperText>
              )}
            </FormGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              height: isDialog ? "80vh" : `calc(86vh - 180px)`,
              width: "inherit",
              "& .gray-color-cell": {
                backgroundColor: "secondary.swaGray3",
              },
            }}
            data-testid="bid-lines-grid"
          >
            <DataGridPro
              sx={{
                border: 0,
                "& div": {
                  "& .MuiDataGrid-footerContainer": {
                    display: "none",
                  },
                  "& .MuiDataGrid-cell": { whiteSpace: "normal !important", textAlign: "left" },
                },
                borderRadius: "0px",
                "& .MuiDataGrid-columnHeaderTitle": {
                  textOverflow: "clip",
                  whiteSpace: "break-spaces",
                  lineHeight: 1.2,
                  textAlign: "left",
                },

                "& .noBids": {
                  backgroundColor: "secondary.swaLightYellow",
                  "&:hover": {
                    backgroundColor: "secondary.swaLightYellow",
                  },
                },
                "& .ineligibleAtOpenRows": {
                  backgroundColor: "secondary.swaLightTurquoise",
                  "&:hover": {
                    backgroundColor: "secondary.swaLightTurquoise",
                  },
                },
              }}
              columns={getAwardTabBidLinesColumns(
                props.handleAwardedNameBidChoicesClick,
                props.handleAwardedNamePermanentBidClick,
                props.bid.unionContract
              )}
              rows={filteredBidLines}
              rowHeight={60}
              getRowHeight={() => "auto"}
              components={{
                LoadingOverlay: customLoadingTableOverlay,
                NoRowsOverlay: CustomNoRowsTableOverlay,
                ErrorOverlay: CustomNoRowsTableOverlay,
              }}
              componentsProps={{
                noRowsOverlay: { pageType: BID_LINES_PAGE_TYPE },
                // errorOverlay: {
                //   errorMessage: props.errorObject.message,
                //   errorCode: props.errorObject.code,
                // },
                // toolbar: { station: props.station },
              }}
              loading={props.isBidLinesLoading}
              disableColumnMenu={true}
              showColumnVerticalBorder={true}
              showCellVerticalBorder={true}
              initialState={{
                pinnedColumns:
                  getUnionContractGivenWorkgroup(props.bid.workgroup) === NON_CONTRACT
                    ? {
                        left: [
                          "__detail_panel_toggle__",
                          "lineNumber",
                          "awardType",
                          "awardedRank",
                          "awardedEid",
                          "awardedName",
                          "ftpt",
                        ],
                        right: ["blockLine"],
                      }
                    : {
                        left: ["lineNumber", "awardType", "awardedRank", "awardedEid", "awardedName", "ftpt"],
                      },
              }}
              getRowClassName={(params) =>
                params.row.awardType === BID_AWARD_TYPE.NO_BID ||
                params.row.awardType === BID_AWARD_TYPE.NOT_ENOUGH_CHOICES
                  ? "noBids"
                  : params.row.awardType?.startsWith(BID_AWARD_TYPE.INELIGIBLE_AT_OPEN)
                  ? "ineligibleAtOpenRows"
                  : ""
              }
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderEnlargeBidLinesDialog = () => {
    return (
      <Dialog onClose={handleEnlargeBidLinesDialogClosed} open={isEnlargeBidLineDialogOpen} fullScreen>
        {renderBidLines(true)}
      </Dialog>
    );
  };

  const renderNoBidsSection = () => {
    return (
      <Grid container>
        <Tabs
          value={selectedTab}
          onChange={handleChangeTab}
          sx={{ width: "inherit", margin: "auto" }}
          variant="fullWidth"
        >
          <Tab sx={{ fontWeight: "bold" }} label={`No Bid (${props.noBidList.length})`}></Tab>
          <Tab sx={{ fontWeight: "bold" }} label={`Ineligible at Open (${props.ineligibleAtOpenList.length})`}></Tab>
        </Tabs>

        {/* No Bid Tab */}
        {selectedTab === 0 && (
          <Grid container sx={{ width: "100%" }}>
            <Grid item xs={12} sx={{ padding: "1rem", textAlign: "left" }}>
              <Typography variant="body">
                Manually award the Employees in this <strong>No Bid</strong> list by clicking the down arrow at the
                beginning of each row.
              </Typography>
            </Grid>
            <Grid
              item
              xs={9}
              sx={{
                paddingLeft: "1rem",
                paddingTop: "10px",
                paddingBottom: "10px",
                display: "flex",
                backgroundColor: "secondary.swaLightestBlue",
                alignItems: "center",
              }}
            >
              <Typography sx={{ paddingRight: "10px" }}>
                Total: <strong>{props.noBidList.length}</strong>
              </Typography>
              <Typography sx={{ paddingRight: "10px" }}>
                FT: <strong>{props.noBidList.filter((emp) => emp.ftpt === FULL_TIME).length}</strong>
              </Typography>
              <Typography>
                PT: <strong>{props.noBidList.filter((emp) => emp.ftpt === PART_TIME).length}</strong>
              </Typography>
            </Grid>
            <Grid
              item
              xs={3}
              sx={{
                paddingTop: "10px",
                paddingBottom: "10px",
                paddingRight: "1rem",
                display: "flex",
                backgroundColor: "secondary.swaLightestBlue",
                justifyContent: "right",
              }}
            >
              <Button
                data-testid="nb-export-button"
                variant="outlined"
                color="primary"
                size="medium"
                sx={{ margin: "5px", width: "120px" }}
                onClick={() => handleNoBidIAOExportBtnClick("No_Bid")}
                disabled={props.noBidList?.length === 0}
                startIcon={<UploadFileIcon />}
              >
                Export
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  height: `calc(85vh - 230px)`,
                  "& .gray-color-cell": {
                    backgroundColor: "secondary.swaGray3",
                  },
                }}
                data-testid="bid-lines-grid"
              >
                <DataGridPro
                  sx={{
                    border: 0,
                    "& div": {
                      "& .MuiDataGrid-footerContainer": {
                        display: "none",
                      },
                    },
                    borderRadius: "0px",
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textOverflow: "clip",
                      whiteSpace: "break-spaces",
                      lineHeight: 1.2,
                      textAlign: "left",
                    },
                  }}
                  columns={getAwardTabNoBidEmpColumns(handleNoBidListChevronClicked, props.bid.status)}
                  rows={props.noBidList}
                  rowHeight={60}
                  components={{
                    LoadingOverlay: customLoadingTableOverlay,
                    NoRowsOverlay: CustomNoRowsTableOverlay,
                    ErrorOverlay: CustomNoRowsTableOverlay,
                  }}
                  componentsProps={{
                    noRowsOverlay: { pageType: BID_AWARD_NO_BID_PAGE_TYPE },
                    // errorOverlay: {
                    //   errorMessage: props.errorObject.message,
                    //   errorCode: props.errorObject.code,
                    // },
                    // toolbar: { station: props.station },
                  }}
                  loading={props.isBidLinesLoading}
                  disableColumnMenu={true}
                  showColumnVerticalBorder={true}
                  showCellVerticalBorder={true}
                  getDetailPanelContent={getNoBidDetailPanelContent}
                  getDetailPanelHeight={getDetailPanelHeight}
                  detailPanelExpandedRowIds={noBidListDetailPanelExpandedRowIds}
                  onDetailPanelExpandedRowIdsChange={handleNoBidListDetailPanelExpandedRowIdsChange}
                />
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Ineligible at Open Tab */}
        {selectedTab === 1 && (
          <Grid container sx={{ width: "100%" }}>
            <Grid item xs={12} sx={{ padding: "1rem", textAlign: "left" }}>
              <Typography variant="body">
                You must manually award all Employees in the <strong>No Bid</strong> list before you can{" "}
                <strong>Award IAO</strong> here.
              </Typography>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                paddingLeft: "1rem",
                paddingTop: "10px",
                paddingBottom: "10px",
                display: "flex",
                backgroundColor: "secondary.swaLightestBlue",
                alignItems: "center",
              }}
            >
              <Typography sx={{ paddingRight: "10px" }}>
                Total: <strong>{props.ineligibleAtOpenList.length}</strong>
              </Typography>
              <Typography sx={{ paddingRight: "10px" }}>
                FT: <strong>{props.ineligibleAtOpenList.filter((emp) => emp.ftpt === FULL_TIME).length}</strong>
              </Typography>
              <Typography>
                PT: <strong>{props.ineligibleAtOpenList.filter((emp) => emp.ftpt === PART_TIME).length}</strong>
              </Typography>
            </Grid>
            <Grid
              item
              md={8}
              sx={{
                paddingRight: "1rem",
                paddingTop: "10px",
                paddingBottom: "10px",
                display: "flex",
                backgroundColor: "secondary.swaLightestBlue",
                justifyContent: "right",
              }}
            >
              <Button
                data-testid="iao-export-button"
                variant="outlined"
                color="primary"
                size="medium"
                sx={{ margin: "5px", marginLeft: "10px", width: "120px" }}
                onClick={() => handleNoBidIAOExportBtnClick("IAO")}
                startIcon={<UploadFileIcon />}
                disabled={props.ineligibleAtOpenList?.length === 0}
              >
                Export
              </Button>
              {!isAwardedBid(props.bid.status) && isNoBidListChanged ? (
                <Tooltip
                  title="You must save your No Bid list changes before you can run the award."
                  arrow
                  placement="bottom"
                >
                  <span>{renderAwardIAOButton()}</span>
                </Tooltip>
              ) : (
                renderAwardIAOButton()
              )}
            </Grid>
            {IAOAwardErrorMessage !== "" && (
              <Grid item xs={12}>
                <ErrorMessage
                  errorMessage={bidDataErrorCodeToErrorMessageMap.IAOBidAwardError.message}
                  errorCode={bidDataErrorCodeToErrorMessageMap.IAOBidAwardError.code}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box
                sx={{
                  height: `calc(85vh - 280px)`,
                  "& .gray-color-cell": {
                    backgroundColor: "secondary.swaGray3",
                  },
                }}
                data-testid="bid-lines-grid"
              >
                <DataGridPro
                  sx={{
                    border: 0,
                    "& div": {
                      "& .MuiDataGrid-footerContainer": {
                        display: "none",
                      },
                    },
                    borderRadius: "0px",
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textOverflow: "clip",
                      whiteSpace: "break-spaces",
                      lineHeight: 1.2,
                      textAlign: "left",
                    },
                  }}
                  columns={getAwardTabNoBidEmpColumns(handleIneligibleAtOpenListChevronClicked, props.bid.status)}
                  rows={props.ineligibleAtOpenList}
                  rowHeight={60}
                  components={{
                    LoadingOverlay: customLoadingTableOverlay,
                    NoRowsOverlay: CustomNoRowsTableOverlay,
                    ErrorOverlay: CustomNoRowsTableOverlay,
                  }}
                  componentsProps={{
                    noRowsOverlay: { pageType: BID_AWARD_NO_BID_PAGE_TYPE },
                    // errorOverlay: {
                    //   errorMessage: props.errorObject.message,
                    //   errorCode: props.errorObject.code,
                    // },
                    // toolbar: { station: props.station },
                  }}
                  loading={props.isBidLinesLoading}
                  disableColumnMenu={true}
                  showColumnVerticalBorder={true}
                  showCellVerticalBorder={true}
                  getDetailPanelContent={getIneligibleAtOpenDetailPanelContent}
                  getDetailPanelHeight={getDetailPanelHeight}
                  detailPanelExpandedRowIds={ineligibleAtOpenListDetailPanelExpandedRowIds}
                  onDetailPanelExpandedRowIdsChange={handleIneligibleAtOpenListDetailPanelExpandedRowIdsChange}
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
      {!props.isBidGroupLoading && !props.isBidLinesLoading ? (
        <>
          {props.isBidGroupTabChanged && renderBidGroupChangesDetectedAlert()}
          {props.isBidLineTabChanged && renderBidLineChangesDetectedAlert()}
          {getUnAssignedBlockLinesCount(props.bidLines) > 0 && renderUnAssignedBlockLinesAlert()}
          {isBidAwardAvailable() ? renderPostAwardResults() : renderPreAwardChecks()}
          {isEnlargeBidLineDialogOpen && renderEnlargeBidLinesDialog()}
          {isReRunAwardWarningDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isReRunAwardWarningDialogOpen}
              title="Re-run Award"
              message="You are running the award again. This will delete the existing award results."
              handleNoButtonClicked={() => setIsReRunAwardWarningDialogOpen(false)}
              handleYesButtonClicked={handleReRunAwardConfirm}
              isYesLoading={isAwardRunning}
              noTitle="Cancel"
              yesTitle="Re-Run Award"
            />
          )}
          {isReRunIAOAwardConfirmationDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isReRunIAOAwardConfirmationDialogOpen}
              title="Re-run Award Ineligible at Open"
              message="You are re-running the award of the IAO Employees. This will delete the existing IAO results."
              handleNoButtonClicked={() => setIsReRunIAOAwardConfirmationDialogOpen(false)}
              handleYesButtonClicked={handleReRunIAOAwardConfirm}
              isYesLoading={isIAOAwardRunning}
              noTitle="Cancel"
              yesTitle="Re-Run Award IAO"
            />
          )}
          {isIAOOverwriteCheckDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isIAOOverwriteCheckDialogOpen}
              title="No Bid List Change After Ineligible at Open"
              message="You are changing the No Bid list after Ineligible at Open has been worked on. This will delete the existing IAO results. Are you sure you want to continue?"
              handleNoButtonClicked={() => setIsIAOOverwriteCheckDialogOpen(false)}
              handleYesButtonClicked={handleIAOOverwriteConfirm}
              noTitle="Cancel"
              yesTitle="Continue"
            />
          )}
          {isPublishConfirmationDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isPublishConfirmationDialogOpen}
              title="Publish Shift Bid"
              message={BID_PUBLISH_DIALOG_MESSAGE(props.bid.station, props.bid.workgroup, props.schedDates)}
              handleNoButtonClicked={() => setIsPublishConfirmationDialogOpen(false)}
              handleYesButtonClicked={onPublishConfirm}
              isYesLoading={showPublishLoader}
              noTitle="Cancel"
              yesTitle="PUBLISH"
            />
          )}
          {isSuccessDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isSuccessDialogOpen}
              title={"Bid Confirmation"}
              message={"The bid has been successfully published!"}
              hasError={publishErrors !== ""}
              error={publishErrors}
              redirectTo={publishErrors === "" && props.bidAdminPath}
              handleOKClick={() => {
                setPublishErrors("");
                setIsSuccessDialogOpen(false);
                publishErrors !== "" && props.loadBidData();
              }}
            />
          )}
        </>
      ) : (
        <div style={{ width: "100%" }}>
          <LinearProgress />
        </div>
      )}
    </Grid>
  );
};

ShiftBidAward.propTypes = {
  bid: PropTypes.object.isRequired,
  setBid: PropTypes.func.isRequired,
  bidLines: PropTypes.array.isRequired,
  setBidLines: PropTypes.func.isRequired,
  isBidLinesLoading: PropTypes.bool.isRequired,
  isBidLineTabChanged: PropTypes.bool.isRequired,
  bidGroup: PropTypes.array.isRequired,
  setBidGroup: PropTypes.func.isRequired,
  isBidGroupLoading: PropTypes.bool.isRequired,
  isBidGroupTabChanged: PropTypes.bool.isRequired,
  schedDates: PropTypes.array.isRequired,
  bidName: PropTypes.string,
  noBidList: PropTypes.array.isRequired,
  ineligibleAtOpenList: PropTypes.array.isRequired,
  bidAdminPath: PropTypes.string.isRequired,
  isFormChanged: PropTypes.bool.isRequired,
  loadBidData: PropTypes.func.isRequired,
  handleAwardedNameBidChoicesClick: PropTypes.func.isRequired,
  handleAwardedNamePermanentBidClick: PropTypes.func.isRequired,
  currentRequestId: PropTypes.object.isRequired,
};

export default ShiftBidAward;
